<template>
  <div class="article-feedback">
    <div class="buttons-container">
      <ThemeButton
        v-for="button in buttons"
        :key="button.id"
        :outlined="!button.filled"
        class="feedback-button px-5 py-2"
        @click="button.onClick"
      >
        <component :is="button.prependIcon" v-if="button.prependIcon" class="prepend-icon" />

        <span>{{ button.text }}</span>

        <component :is="button.appendIcon" v-if="button.appendIcon" class="append-icon" />
      </ThemeButton>
    </div>
  </div>
</template>

<script lang="ts">
import ThemeButton from './ThemeButton.vue';

import { Component, Prop, Vue } from 'vue-property-decorator';
import { ArticleAction, ConnectToAgent } from '@/common/bot/types';
import FaAngleRight from './icons/FaAngleRight.vue';
import FaAngleLeft from './icons/FaAngleLeft.vue';
import CustomerSupportIcon from './icons/CustomerSupportIcon.vue';
import { namespace } from 'vuex-class';

const userModule = namespace('user');

@Component({ components: { ThemeButton } })
export default class ArticleFeedback extends Vue {
  @Prop() readonly articleAction!: ArticleAction | null;
  @Prop(Boolean) readonly canConnectToSupport!: boolean;
  @Prop(Boolean) readonly enableMetadataRules!: boolean;

  @userModule.State('isLogin') isLogin!: boolean;

  loading = false;
  loadedConfig: ConnectToAgent | null = null;

  get buttons() {
    const buttons = [
      {
        id: 'back',
        text: this.$t('global.back'),
        prependIcon: FaAngleLeft,
        onClick: this.emitBackClick,
      },
    ];

    const { articleAction, canConnectToSupport, enableMetadataRules } = this;
    if (!canConnectToSupport && !articleAction && !enableMetadataRules) return buttons;

    // If we have connect, it does not matter if we have action, Connect
    // Will have first preference here.
    if (canConnectToSupport || (enableMetadataRules && this.isLogin)) {
      return [
        ...buttons,
        {
          id: 'need-more-help-btn',
          text: canConnectToSupport
            ? this.t('chat.sys.navigation.contact_support')
            : this.t('article.feedback.button.need_more_help'),
          onClick: this.emitConnectToAgent,
          prependIcon: CustomerSupportIcon,
        },
      ];
    }

    // No connect to support but has an article action.
    if (articleAction) {
      return [
        ...buttons,
        {
          id: 'article-action-btn-' + articleAction.action,
          text: articleAction.actionTitle,
          appendIcon: FaAngleRight,
          onClick: this.emitActionClick,
          filled: true,
        },
      ];
    }
    // SNR
    return buttons;
  }

  emitBackClick() {
    this.$emit('back');
  }

  emitActionClick() {
    this.$emit('action');
  }
  emitConnectToAgent() {
    this.$emit('connect');
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/variable.scss';

.article-feedback {
  position: sticky;
  bottom: 0px;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 0 2rem 2rem;
  background-color: $article-background;

  .title {
    @extend .text-bold;
    font-size: $size-m;
    padding: 0 0 17px 0;
    color: $article-title-color;
  }

  .buttons-container {
    width: 100%;

    display: flex;
    justify-content: center;
    gap: 2rem;

    border-top: 1px solid $article-feedback-border-color;
    padding-top: 2rem;
  }

  button {
    padding: 0.8rem 1rem;
    font-size: 12px;
    min-width: 80px;
    font-weight: 500;
    border-radius: 2px;
  }

  .feedback-button {
    display: flex;
    align-items: center;
    justify-content: center;
    .prepend-icon {
      margin-right: 4px;
    }
    .append-icon {
      margin-left: 4px;
    }
  }
}
</style>
